import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { STAGE_LABELS } from '../../contacts/types';
import { useSetStageCadence, useStageCadences } from '../hooks/useOutreach';
import { CADENCE_STAGES, type OutreachContactStage } from '../types';

/** osprey-api's DEFAULT_FOLLOWUP_INTERVAL_DAYS — what an unconfigured stage falls back to. */
const DEFAULT_INTERVAL_DAYS = 7;

/** SetFollowUpStageCadenceDto bounds: @IsInt @Min(1) @Max(90). */
const valid = (value: string) => {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 90;
};

function StageRow({
  stage,
  current,
  canManage,
}: {
  stage: OutreachContactStage;
  current: number | undefined;
  canManage: boolean;
}) {
  const setCadence = useSetStageCadence();
  const effective = current ?? DEFAULT_INTERVAL_DAYS;
  // Seeded once per saved value — the parent re-keys the row when the stored cadence changes.
  const [value, setValue] = useState(String(effective));

  const dirty = value !== String(effective);

  return (
    <div className="flex flex-wrap items-end gap-3 py-3 border-b border-border last:border-b-0">
      <div className="w-44">
        <p className="text-sm font-medium text-foreground">{STAGE_LABELS[stage]}</p>
        <p className="text-xs text-muted-foreground">
          {current === undefined ? `Default (${DEFAULT_INTERVAL_DAYS} days)` : 'Configured'}
        </p>
      </div>
      <Input
        type="number"
        min={1}
        max={90}
        className="w-28 min-h-10 lg:min-h-0"
        aria-label={`${STAGE_LABELS[stage]} follow-up interval in days`}
        value={value}
        disabled={!canManage}
        onChange={(e) => setValue(e.target.value)}
      />
      <span className="text-sm text-muted-foreground pb-2.5">days</span>
      {canManage && (
        <Button
          size="sm"
          className="h-[40px]"
          disabled={!dirty || !valid(value) || setCadence.isPending}
          loading={setCadence.isPending}
          onClick={() => setCadence.mutate({ stage, intervalDays: Number(value) })}
        >
          Save
        </Button>
      )}
    </div>
  );
}

/**
 * Follow-up cadence per pipeline stage — "configured cadence" in the PE-2188 follow-up queue.
 * A contact can override its stage's cadence from its own page. Only stages the due queue
 * actually considers are listed; SIGNED / ACTIVE_MERCHANT / LOST never come due.
 */
export function CadencePanel({ canManage }: { canManage: boolean }) {
  const { data: cadences = [], isLoading, error } = useStageCadences();
  const byStage = new Map(cadences.map((c) => [c.stage, c.intervalDays]));

  return (
    <div className="bg-card rounded-lg border border-border p-6 max-w-[640px]">
      <h2 className="text-base font-semibold text-foreground">Follow-up cadence</h2>
      <p className="text-sm text-muted-foreground mt-0.5 mb-2">
        How many days after the last message a contact in each stage comes due again (1–90).
      </p>
      {error && <p className="text-sm text-red-500">Could not load cadences.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading &&
        !error &&
        CADENCE_STAGES.map((stage) => (
          <StageRow
            key={`${stage}:${byStage.get(stage) ?? 'default'}`}
            stage={stage} current={byStage.get(stage)} canManage={canManage} />
        ))}
    </div>
  );
}
